// src/data/editions/schools.js
//
// SCHOOLS edition: a school division read by school. The people in the data
// are students (grades 7 to 12, with a parent or guardian's consent) and the
// staff who teach them, each checking in privately; the console is read by
// the division and its student services team to see where strain sits,
// school by school, and to place supports. So: no leave forecast and no ROI;
// the organizational leg is the division's own figures (attendance, chronic
// absence, referrals, students seen, access to a school-based mental health
// professional, suspensions, critical incidents, staff absence) and the
// comparison is the division's other schools until a sourced provincial
// reference is on file (SECTOR_REFERENCES.schools is empty on purpose). The
// Alberta Recovery Model tab leads with the focus area "Building better
// futures for our youth". Every figure here is illustrative (summary.source
// = 'demo' drives the console's "Modeled example" badge). DRAFT copy.

const schools = {
    id: 'schools',
    label: 'Schools',

    palette: { deep: '#1E3A5F', blue: '#2A7F9E', yellow: '#F2B134' },

    // The console as a page: the hub sidebar entry, the console sidebar
    // kicker, the header line, and the tabs that make no sense for a division.
    consoleLabel: 'School Wellbeing Insights',
    headerSubtitle: "Student and staff readiness by school, built from private check-ins, the division's own figures and the division's other schools.",
    hiddenTabs: ['cc-predictive', 'cc-roi'],
    // The console's Alberta Recovery Model tab leads with this focus area
    // (ids: access, barriers, facilities, immediate, youth).
    recoveryModel: { lead: 'youth' },

    pageTitles: {
        'cc-overview': 'School Wellbeing Overview',
        'cc-recovery': 'The Alberta Recovery Model: where these figures sit',
        'cc-triangulation': 'Triangulation: Students, the Division, Other Schools',
        'cc-data': "Data Intake: The Division's Own Figures",
        'cc-ai': 'Ask the Data',
        'cc-readiness': 'Readiness & Strain by School',
        'cc-predictive': 'Attendance Outlook',
        'cc-roi': 'Program Cost & Impact',
        'cc-feedback': 'Anonymous Pulse by School',
        default: 'School Wellbeing Insights'
    },

    trendSignalLabel: 'Attendance signal',

    summary: {
        source: 'demo',
        capacityScore: 71,
        capacityTrend: 1,
        workforce: 2400,
        highSignalCohort: 310,
        topPriorityGroup: 'Riverbend High School',
        topPulseTheme: 'Exams and sleep',
        participationRate: 46,
        modeledClaimsAvoidance: 'n/a',
        modeledStdClaims: { low: 0, high: 0, windowDays: 90 },
        modeledLtdTransition: { low: 0, high: 0 },
        leaveProbability: { mid: 0, low: 0, high: 0 },
        outlookTitle: 'Attendance outlook',
        outlookNoun: 'new chronic absences',
        outlookUnit: 'students',
        financialNoun: 'program cost avoidance'
    },

    capacityTrend: [
        { date: '1st', capacity: 72, signal: 18 },
        { date: '4th', capacity: 72, signal: 18 },
        { date: '8th', capacity: 71, signal: 19 },
        { date: '12th', capacity: 70, signal: 21 },
        { date: '16th', capacity: 70, signal: 22 },
        { date: '20th', capacity: 71, signal: 21 },
        { date: '24th', capacity: 71, signal: 21 },
        { date: '28th', capacity: 71, signal: 22 },
        { date: 'Today', capacity: 71, signal: 22 }
    ],

    readiness: {
        scoreLabel: 'School Wellbeing Score',
        signalSubtext: 'exam load + sleep and screens + staff workload',
        groups: [
            { name: 'Riverbend High School', pressure: 83, note: 'Exam season on short sleep; output holding, recovery not', responses: 212 },
            { name: 'Teaching & School Staff', pressure: 74, note: 'Workload and cover for absent colleagues', responses: 64 },
            { name: 'Prairie View High School', pressure: 72, note: 'Steady; friendships the most-cited theme', responses: 186 },
            { name: 'Westwood Junior High', pressure: 66, note: 'Screens late into the night', responses: 141 },
            { name: 'Northside Junior High', pressure: 61, note: 'Within normal range', responses: 128 },
            { name: 'Outreach & Alternative Programs', pressure: 70, note: 'Within normal range', responses: 4 } // below floor
        ],
        signals: [
            { label: 'Self-reported strain trend', value: '+6%', tier: 'Intermediate', tone: 'amber' },
            { label: 'Chronic absence share', value: '+3%', tier: 'Lagging', tone: 'amber' },
            { label: 'Referrals to a school counsellor', value: '+8%', tier: 'Lagging', tone: 'amber' },
            { label: 'Students saying a friend is struggling', value: 'Rising', tier: 'Leading', tone: 'amber' }
        ]
    },

    feedback: {
        scopes: [
            { id: 'org', label: 'Division-wide', responses: 735 },
            { id: 'riverbend', label: 'Riverbend High School', responses: 212 },
            { id: 'prairieview', label: 'Prairie View High School', responses: 186 },
            { id: 'westwood', label: 'Westwood Junior High', responses: 141 },
            { id: 'northside', label: 'Northside Junior High', responses: 128 },
            { id: 'staff', label: 'Teaching & School Staff', responses: 64 },
            { id: 'outreach', label: 'Outreach & Alternative Programs', responses: 4 } // below floor -> suppressed
        ],
        submissions: [
            { id: 1, time: '2 hours ago', theme: 'Exams and sleep', sentiment: 'risk', scope: 'riverbend',
              text: 'Three tests this week and I have been up past one every night. I am not learning anything, I am just getting through.' },
            { id: 2, time: '5 hours ago', theme: 'A friend who is struggling', sentiment: 'risk', scope: 'prairieview',
              text: 'My friend has stopped coming to practice and goes quiet in the group chat. I do not know what to say or who to tell.' },
            { id: 3, time: 'Yesterday', theme: 'Sleep and screens', sentiment: 'positive', scope: 'westwood',
              text: 'The check-in told me my sleep was the problem, not me. I put the phone in the kitchen at ten and the week was better.' },
            { id: 4, time: 'Yesterday', theme: 'Staff workload', sentiment: 'risk', scope: 'staff',
              text: 'Covering two classes for a colleague on leave, on top of report cards. The students notice when we are running on empty.' },
            { id: 5, time: '2 days ago', theme: 'Friendships', sentiment: 'positive', scope: 'northside',
              text: 'The friendships page helped me fix things with my friend instead of avoiding her. A small thing that was not small.' }
        ],
        sentiment: {
            positiveSummary: 'The private check-in and the sleep and screens content are read as fair and useful by students.',
            emergingTheme: 'Exam load on short sleep and worry about a friend who is struggling are the most frequent risk notes; staff name workload and cover for absent colleagues.'
        }
    },

    // Hidden for this edition (hiddenTabs); kept so the shape is complete.
    predictive: {
        title: 'The Attendance Funnel',
        intro: 'In the modeled baseline, a share of students under sustained strain would be expected to slide into chronic absence, and a smaller share to need a referral. Directional, not a forecast; it needs the division\'s own attendance and referral figures beside it before any claim.',
        lever: 'Earlier routing to the school counsellor',
        ownLabel: 'Chronic absence share',
        tiers: [
            { value: '2,400', label: 'Enrolled students', source: 'observed', tone: 'neutral', width: 100 },
            { value: '310', label: 'High-strain cohort', source: 'observed', tone: 'brand', width: 80 },
            { value: '60-90', label: 'Modeled new chronic absences - term', source: 'modeled', tone: 'amber', width: 60 },
            { value: '25-40', label: 'Modeled counsellor referrals', source: 'modeled', tone: 'amber', width: 44 },
            { value: '6-10', label: 'Critical incidents - tail', source: 'modeled', tone: 'muted', width: 30 }
        ],
        cards: [
            { title: 'Strain-absence association', value: '~3x', valueTone: 'amber', level: 'Intermediate', source: 'modeled', dark: false,
              subtext: 'Modeled association - not causal',
              body: 'Schools with a higher high-strain share show more chronic absence. Directional, and confirmed only against the division\'s own attendance figures.' },
            { title: 'Modeled referral probability', value: '12%', valueTone: 'white', level: 'Lagging', source: 'modeled', dark: true,
              subtext: 'Midpoint - 9-15% band',
              body: 'Modeled share of high-strain students who reach a school counsellor within a term. The band narrows as the division\'s own figures are added.' },
            { title: 'Routing lift', value: '+30%', valueTone: 'emerald', level: 'Leading', source: 'modeled', dark: false,
              subtext: 'Modeled, not yet observed in a division',
              body: 'Students who used the check-in tapped Talk to Someone more often than those who only read content: a leading indicator, shown as an association.' }
        ],
        footer: {
            lead: 'These are models, not forecasts.',
            note: 'Projections are reported by school only, never for a student. Attendance and referrals are lagging outcomes that need the division\'s own figures beside them before any causal claim.'
        }
    },

    // Hidden for this edition (hiddenTabs); the terms still name the division
    // wherever the console needs a noun for the organizational leg.
    roi: {
        currency: 'CAD',
        locale: 'en-CA',
        benchSource: 'sector reference',
        replacementMultiple: 0,
        turnoverRate: 0,
        termLabels: {
            organization: 'the division',
            lostTime: 'Earlier support',
            lostTimeBasis: 'Not a cost model: a school division measures attendance, referrals and access.'
        }
    },

    copilot: { exampleScope: 'Riverbend High School' }
};

export default schools;
